import { getInstance } from '@/auth'

export default {
  namespaced: true,

  state: () => ({
    isOpen: false,
    sessionId: null,
    turns: [],           // [{ id, role, content, timestamp }]
    isStreaming: false,
    streamingContent: '', // accumulates tokens during active stream
    error: null,
    // History
    isHistoryOpen: false,
    sessions: [],        // [{ id, createdAt, lastActivityAt, previewText }]
    isLoadingHistory: false
  }),

  mutations: {
    SET_OPEN (state, val) { state.isOpen = val },
    SET_SESSION_ID (state, id) { state.sessionId = id },
    ADD_TURN (state, turn) { state.turns.push(turn) },
    SET_TURNS (state, turns) { state.turns = turns },
    SET_STREAMING (state, val) { state.isStreaming = val },
    APPEND_STREAMING_CONTENT (state, text) { state.streamingContent += text },
    SET_STREAMING_CONTENT (state, text) { state.streamingContent = text },
    SET_ERROR (state, err) { state.error = err },
    SET_HISTORY_OPEN (state, val) { state.isHistoryOpen = val },
    SET_SESSIONS (state, sessions) { state.sessions = sessions },
    SET_LOADING_HISTORY (state, val) { state.isLoadingHistory = val },
    REMOVE_SESSION (state, id) { state.sessions = state.sessions.filter(s => s.id !== id) },
    CLEAR_SESSION (state) {
      state.sessionId = null
      state.turns = []
      state.streamingContent = ''
      state.error = null
    }
  },

  actions: {
    toggle ({ commit, state }) { commit('SET_OPEN', !state.isOpen) },
    open ({ commit }) { commit('SET_OPEN', true) },
    close ({ commit }) { commit('SET_OPEN', false) },
    newSession ({ commit }) {
      commit('CLEAR_SESSION')
      commit('SET_HISTORY_OPEN', false)
    },

    async toggleHistory ({ commit, state, rootState }) {
      const opening = !state.isHistoryOpen
      commit('SET_HISTORY_OPEN', opening)
      if (opening) {
        const companyId = rootState.userCurrentCompany && rootState.userCurrentCompany.companyId
        if (!companyId) return
        commit('SET_LOADING_HISTORY', true)
        try {
          const token = await getInstance().getTokenSilently()
          const res = await fetch(`${process.env.API_URL}api/company/${companyId}/chat/sessions`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (res.ok) {
            const data = await res.json()
            commit('SET_SESSIONS', data.sessions || data || [])
          }
        } catch (err) {
          console.error('[chatbot] loadHistory error:', err)
        } finally {
          commit('SET_LOADING_HISTORY', false)
        }
      }
    },

    async resumeSession ({ commit, rootState }, { sessionId }) {
      const companyId = rootState.userCurrentCompany && rootState.userCurrentCompany.companyId
      if (!companyId) return
      try {
        const token = await getInstance().getTokenSilently()
        const res = await fetch(`${process.env.API_URL}api/company/${companyId}/chat/session/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          commit('CLEAR_SESSION')
          commit('SET_SESSION_ID', data.id)
          commit('SET_TURNS', (data.turns || []).map((t, i) => ({
            id: t.id || i,
            role: t.role,
            content: t.content,
            timestamp: t.timestamp
          })))
          commit('SET_HISTORY_OPEN', false)
        }
      } catch (err) {
        console.error('[chatbot] resumeSession error:', err)
      }
    },

    async deleteSession ({ commit, rootState }, { sessionId }) {
      const companyId = rootState.userCurrentCompany && rootState.userCurrentCompany.companyId
      if (!companyId) return
      try {
        const token = await getInstance().getTokenSilently()
        const res = await fetch(`${process.env.API_URL}api/company/${companyId}/chat/session/${sessionId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          commit('REMOVE_SESSION', sessionId)
        }
      } catch (err) {
        console.error('[chatbot] deleteSession error:', err)
      }
    },

    async sendMessage ({ commit, state, rootState }, { message }) {
      if (state.isStreaming) return

      const company = rootState.userCurrentCompany
      const companyId = company && company.companyId
      if (!companyId) return

      const companyName = (company && company.companyName) || ''

      commit('ADD_TURN', {
        id: Date.now(),
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      })
      commit('SET_STREAMING', true)
      commit('SET_STREAMING_CONTENT', '')
      commit('SET_ERROR', null)

      const sessionId = state.sessionId || 'new'
      const url = `${process.env.CHAT_STREAM_URL}api/company/${companyId}/chat/${sessionId}/message`

      try {
        const token = await getInstance().getTokenSilently()
        const bodyStr = JSON.stringify({ message, companyName, companyId, sessionId })

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'X-Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'text/event-stream'
          },
          body: bodyStr
        })

        if (!response.ok) {
          let errMsg = `Request failed: ${response.status}`
          try {
            const body = await response.json()
            errMsg = body.message || errMsg
          } catch (_) {}
          throw new Error(errMsg)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) { // eslint-disable-line no-constant-condition
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() // keep incomplete last line in buffer

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const raw = line.slice(6).trim()
            if (!raw) continue
            let chunk
            try { chunk = JSON.parse(raw) } catch (_) { continue }

            // Capture sessionId returned on first SSE chunk
            if (chunk.sessionId && !state.sessionId) {
              commit('SET_SESSION_ID', chunk.sessionId)
            }

            switch (chunk.type) {
              case 'text':
                commit('APPEND_STREAMING_CONTENT', chunk.content || '')
                break
              case 'error':
                commit('SET_ERROR', chunk.content || 'An error occurred.')
                break
              // 'tool_start', 'tool_result', 'done' — handled implicitly (stream ends)
            }
          }
        }
      } catch (err) {
        commit('SET_ERROR', err.message || 'Failed to connect. Please try again.')
        console.error('[chatbot] sendMessage error:', err)
      } finally {
        // Commit accumulated streaming content as a completed assistant turn
        const completed = state.streamingContent
        commit('SET_STREAMING', false)
        if (completed) {
          commit('ADD_TURN', {
            id: Date.now() + 1,
            role: 'assistant',
            content: completed,
            timestamp: new Date().toISOString()
          })
          commit('SET_STREAMING_CONTENT', '')
        }
      }
    }
  },

  getters: {
    isOpen: state => state.isOpen,
    turns: state => state.turns,
    isStreaming: state => state.isStreaming,
    streamingContent: state => state.streamingContent,
    error: state => state.error,
    sessionId: state => state.sessionId,
    isHistoryOpen: state => state.isHistoryOpen,
    sessions: state => state.sessions,
    isLoadingHistory: state => state.isLoadingHistory
  }
}




// WEBPACK FOOTER //
// ./src/store/modules/chatbot.js